import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";

import withPrefersReducedMotion, {
    type WithoutPrefersReducedMotion,
    type WithPrefersReducedMotionProps,
} from "#data-access/with-prefers-reduced-motion.tsx";

import caretDownIcon from "@phosphor-icons/core/bold/caret-down-bold.svg";

type OwnProps = {
    // Label for the button.
    label: React.ReactNode;
    // Whether the content is expanded. Rotates the caret icon.
    isExpanded: boolean;
    // Called when the button is clicked.
    onClick?: () => void;
    testId?: string | null | undefined;
};

type Props = OwnProps & WithPrefersReducedMotionProps;

const BORDER_SIZE = 1;

/**
 * A button to toggle a NavSubsection, with a cute animated caret on the left!
 *
 * Generally, it's probably not great to use this component directly. Use a
 * <NavSubsection /> instead. (But this component could be a good candidate for
 * generalization into Wonder Blocks someday!)
 */
function ToggleButton({
    label,
    isExpanded,
    onClick,
    testId,
    prefersReducedMotion,
}: Props): React.ReactElement {
    const transitionTime = prefersReducedMotion ? 0 : 0.3;

    return (
        <button
            className={css(styles.toggleButton)}
            onClick={onClick}
            data-testid={testId}
        >
            <View style={styles.toggleIconWrapper}>
                <PhosphorIcon
                    icon={caretDownIcon}
                    color={color.offBlack}
                    size="small"
                    style={{
                        transition: `transform ${transitionTime}s`,
                        transform: `rotate(${isExpanded ? "0deg" : "-90deg"})`,
                    }}
                />
            </View>
            <LabelMedium style={styles.toggleLabel}>{label}</LabelMedium>
        </button>
    );
}

const styles = StyleSheet.create({
    // HACK(mdr): These styles are close to the Wonder Blocks <Link />, but not
    //     quite. And we need button semantics for a11y, which <Link /> doesn't
    //     support. For now, we've hardcoded these styles, but that makes us
    //     visually fragile if Wonder Blocks link styles change!
    //
    //     See discussion in D52476.
    toggleButton: {
        color: color.offBlack,
        display: "block",
        width: "100%",
        position: "relative",
        paddingTop: spacing.small_12 - BORDER_SIZE,
        paddingBottom: spacing.small_12 - BORDER_SIZE,
        paddingRight: 0,
        backgroundColor: "transparent",
        cursor: "pointer",
        borderRadius: spacing.xxxSmall_4,
        border: `${BORDER_SIZE}px solid transparent`,
        ":hover": {
            textDecoration: "none",
            color: color.offBlack64,
        },
        ":focus": {
            borderColor: color.blue,
            outline: 0,
        },
        paddingLeft: spacing.large_24,
    },
    // We use a wrapper element instead of applying these styles directly to
    // the icon, because the `paddingRight` shifts the "center" of the element,
    // which is important for rotation. Instead, we apply the rotation to the
    // icon directly, because it's a perfect square and has no extra padding.
    toggleIconWrapper: {
        // The icon leaks into the left-hand gutter.
        position: "absolute",
        // This icon doesn't look quite centered at 0px, shifting this 1px
        // to the right makes it look centered
        left: 1,
        // Use padding to add extra space between the icon and the text. By
        // using padding instead of `margin` or extra pixels in `right`, we
        // ensure that the space is clickable as part of the button, instead
        // of creating non-clickable space between the text and icon.
        paddingRight: spacing.xSmall_8,
        // This needs to be vertically centered so this is positioned
        // at 50% - 8px. (8px is half the height of the icon)
        top: "calc(50% - 8px)",
    },
    toggleLabel: {
        textAlign: "left",
    },
});

type ExportProps = WithoutPrefersReducedMotion<
    JSX.LibraryManagedAttributes<
        typeof ToggleButton,
        React.ComponentProps<typeof ToggleButton>
    >
>;

export default withPrefersReducedMotion(
    ToggleButton,
) as React.ComponentType<ExportProps>;
